import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Archive,
  BarChart3,
  Bell,
  Boxes,
  ChevronDown,
  CircleDollarSign,
  ClipboardCheck,
  FileCheck2,
  LayoutDashboard,
  LogOut,
  Map,
  Menu,
  Moon,
  PackageOpen,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Sprout,
  Sun,
  Scale,
  Tractor,
  UserRoundPlus,
  Users,
  UsersRound,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BrandMark } from "@/components/BrandMark";
import { SyncStatusControl } from "@/components/SyncStatusControl";
import { useAuth } from "@/services/supabase";

const dashboardNav = { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard };

const navGroups = [
  {
    id: "registry",
    label: "Registry",
    icon: UsersRound,
    items: [
      { label: "Farmer Registry", to: "/farmers", icon: UsersRound },
      { label: "Farmer Groups", to: "/farmer-groups", icon: Users },
      { label: "Agreements & Records", to: "/agreements", icon: FileCheck2 },
      { label: "Farmer Activity", to: "/transactions", icon: ClipboardCheck },
    ],
  },
  {
    id: "farms-agronomy",
    label: "Farms & Agronomy",
    icon: Tractor,
    items: [
      { label: "Farms", to: "/farms", icon: Tractor },
      { label: "Field Visits", to: "/visits", icon: ClipboardCheck },
      { label: "GIS & Traceability", to: "/gis-traceability", icon: Map, future: true },
    ],
  },
  {
    id: "inputs-stock",
    label: "Inputs & Stock",
    icon: Boxes,
    items: [
      { label: "Input Catalogue", to: "/inputs", icon: PackageOpen },
      { label: "Nursery Management", to: "/nursery", icon: Sprout },
      { label: "Inventory", to: "/inventory", icon: Boxes },
      { label: "Distributions", to: "/distributions", icon: PackageOpen },
    ],
  },
  {
    id: "harvest",
    label: "Harvest",
    icon: Sprout,
    items: [
      { label: "Harvest Operations", to: "/harvest", icon: Sprout },
    ],
  },
  {
    id: "accounts",
    label: "Accounts",
    icon: CircleDollarSign,
    items: [
      { label: "Accounts & Farmer Payments", to: "/accounts", icon: CircleDollarSign },
      { label: "General Ledger", to: "/finance", icon: FileCheck2 },
    ],
  },
  {
    id: "commercial",
    label: "Commercial",
    icon: Scale,
    items: [
      { label: "Commercial Hub", to: "/commercial", icon: Scale },
    ],
  },
  {
    id: "people-resources",
    label: "People & Resources",
    icon: Users,
    items: [
      { label: "Staff & Users", to: "/staff", icon: Users },
      { label: "Assets", to: "/assets", icon: Archive },
      { label: "Documents", to: "/documents", icon: FileCheck2 },
    ],
  },
  {
    id: "insights-admin",
    label: "Insights & Admin",
    icon: BarChart3,
    items: [
      { label: "Reports", to: "/reports", icon: BarChart3 },
      { label: "Governance", to: "/governance", icon: FileCheck2, future: true },
      { label: "Settings", to: "/settings", icon: Settings },
    ],
  },
];

const pathMatches = (pathname: string, target: string) =>
  pathname === target || (target !== "/dashboard" && pathname.startsWith(`${target}/`));

const pageTitles: Record<string, string> = {
  "/dashboard": "Field operations overview",
  "/farmers": "Farmer registry",
  "/farmers/new": "Register farmer",
  "/farms": "Farms",
  "/farmer-groups": "Farmer groups",
  "/visits": "Field visits and agronomy",
  "/visits/new": "Plan field visit",
  "/inputs": "Input catalogue",
  "/nursery": "Nursery management",
  "/distributions": "Input distributions",
  "/harvest": "Harvest and aggregation",
  "/transactions": "Farmer activity",
  "/documents": "Documents and media",
  "/staff": "Staff and users",
  "/staff/farmer-allocations": "Farmer assignment and allocation",
  "/conflicts": "Manual conflict review",
  "/inventory": "Stock balances and movements",
  "/assets": "Asset register",
  "/accounts": "Accounts and farmer payments",
  "/finance": "Accounts — general ledger",
  "/commercial": "Commercial — buyers, sales and investors",
  "/agreements": "Agreements and records",
  "/reports": "Management reports",
  "/gis-traceability": "GIS and traceability",
  "/governance": "Governance foundation",
  "/settings": "Settings preview",
};

const NavigationContent = ({
  compact,
  activePath,
  onNavigate,
  onRequestExpand,
}: {
  compact: boolean;
  activePath: string;
  onNavigate?: () => void;
  onRequestExpand?: () => void;
}) => {
  const activeGroupId = useMemo(
    () => navGroups.find((group) => group.items.some((item) => pathMatches(activePath, item.to)))?.id,
    [activePath],
  );
  const [openGroups, setOpenGroups] = useState<string[]>(() => activeGroupId ? [activeGroupId] : ["registry"]);

  useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups((current) => current.includes(activeGroupId) ? current : [...current, activeGroupId]);
  }, [activeGroupId]);

  const toggleGroup = (groupId: string) => {
    setOpenGroups((current) =>
      current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId],
    );
  };

  const DashboardIcon = dashboardNav.icon;

  return (
    <div className="flex h-full flex-col">
      <div className={`brand-panel relative flex items-center overflow-hidden ${compact ? "h-24 justify-center px-2" : "h-[150px] px-4"}`}>
        <Link to="/dashboard" onClick={onNavigate} className="relative z-10 flex w-full justify-center" data-testid="sidebar-brand-link">
          <BrandMark compact={compact} />
        </Link>
      </div>

      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-2" aria-label="Primary navigation" data-testid="sidebar-primary-nav">
          <Tooltip>
            <TooltipTrigger asChild>
              <NavLink
                to={dashboardNav.to}
                onClick={onNavigate}
                data-testid="nav-dashboard"
                className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link-active" : ""} ${compact ? "justify-center px-2" : ""}`}
              >
                <DashboardIcon className="size-[18px] shrink-0" aria-hidden="true" />
                {!compact && <span>Dashboard</span>}
              </NavLink>
            </TooltipTrigger>
            {compact && <TooltipContent side="right">Dashboard</TooltipContent>}
          </Tooltip>

          {!compact && (
            <p className="px-3 pt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-sidebar-foreground/45">
              Workspaces
            </p>
          )}

          {navGroups.map((group) => {
            const GroupIcon = group.icon;
            const open = openGroups.includes(group.id);
            const active = group.id === activeGroupId;

            if (compact) {
              return (
                <Tooltip key={group.id}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className={`sidebar-group-button justify-center px-2 ${active ? "sidebar-group-button-active" : ""}`}
                      onClick={() => {
                        setOpenGroups([group.id]);
                        onRequestExpand?.();
                      }}
                      aria-label={group.label}
                      data-testid={`nav-group-${group.id}`}
                    >
                      <GroupIcon className="size-[18px]" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">{group.label}</TooltipContent>
                </Tooltip>
              );
            }

            return (
              <div key={group.id} className="space-y-1" data-testid={`nav-group-${group.id}`}>
                <button
                  type="button"
                  className={`sidebar-group-button ${active ? "sidebar-group-button-active" : ""}`}
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={open}
                >
                  <GroupIcon className="size-[18px] shrink-0" />
                  <span className="flex-1 text-left">{group.label}</span>
                  <ChevronDown className={`size-4 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="sidebar-group-children">
                    {group.items.map((item) => {
                      const ItemIcon = item.icon;
                      const future = "future" in item && item.future;
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={onNavigate}
                          data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link-active" : ""}`}
                        >
                          <ItemIcon className="size-4 shrink-0" />
                          <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                            <span className="truncate">{item.label}</span>
                            {future && <span className="rounded-full border border-primary/10 bg-primary/5 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-sidebar-foreground/55">Later</span>}
                          </span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      <div className="h-4 border-t border-sidebar-border/60" aria-hidden="true" />
    </div>
  );
};

export const AppShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { aczonContext, user, signOut } = useAuth() ?? { aczonContext: null, user: null, signOut: async () => {} };
  const [compact, setCompact] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  const currentTitle = useMemo(() => {
    if (location.pathname.startsWith("/farmers/") && location.pathname !== "/farmers/new") return "Farmer profile";
    if (location.pathname.startsWith("/modules/")) return "Module preview";
    return pageTitles[location.pathname] || "ARISE operations";
  }, [location.pathname]);

  const displayName = user?.email ? user.email.split("@")[0].replace(/[._-]/g, " ") : "Staff";
  const displayInitials = displayName.split(" ").map((w) => w[0]?.toUpperCase()).join("").slice(0, 2) || "ST";
  const roleLabel = aczonContext?.role_code ? aczonContext.role_code.replace(/_/g, " ") : "Staff";

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <TooltipProvider delayDuration={250}>
      <div className="min-h-screen bg-background text-foreground" data-testid="app-shell">
        <aside className={`fixed inset-y-0 left-0 z-sidebar hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-[10px_0_36px_hsl(144_75%_12%/0.09)] transition-[width] duration-200 lg:block ${compact ? "w-[84px]" : "w-[296px]"}`} data-testid="desktop-sidebar">
          <NavigationContent compact={compact} activePath={location.pathname} onRequestExpand={() => setCompact(false)} />
          <Button variant="outline" size="icon" onClick={() => setCompact((value) => !value)} className="absolute -right-4 top-[118px] size-8 rounded-full border-primary/15 bg-card shadow-soft" aria-label={compact ? "Expand navigation" : "Collapse navigation"} data-testid="sidebar-collapse-button">
            {compact ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
          </Button>
        </aside>

        <div className={`min-h-screen transition-[padding] duration-200 ${compact ? "lg:pl-[84px]" : "lg:pl-[296px]"}`}>
          <header className="app-header-glow sticky top-0 z-header border-b border-border/70 bg-card/95 backdrop-blur-xl" data-testid="app-context-header">
            <div className="flex h-16 items-center gap-3 px-4 md:px-6 xl:px-8">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="lg:hidden" aria-label="Open navigation" data-testid="mobile-menu-button"><Menu className="size-5" /></Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[304px] border-sidebar-border bg-sidebar p-0 text-sidebar-foreground" data-testid="mobile-navigation-sheet">
                  <NavigationContent compact={false} activePath={location.pathname} onNavigate={() => setMobileOpen(false)} />
                </SheetContent>
              </Sheet>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="organization-context">
                  <span className="hidden sm:inline">ACZON Farms Ltd</span><span className="hidden sm:inline">/</span><span className="font-semibold text-foreground">ARISE</span>
                </div>
                <p className="mt-0.5 truncate text-sm font-semibold text-foreground" data-testid="current-page-context">{currentTitle}</p>
              </div>

              <SyncStatusControl />
              <Button variant="ghost" size="icon" onClick={() => setDark((value) => !value)} aria-label={dark ? "Use light theme" : "Use dark theme"} data-testid="theme-toggle-button">
                {dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
              </Button>
              <Button variant="ghost" size="icon" aria-label="Notifications" data-testid="notifications-button" className="relative">
                <Bell className="size-4" /><span className="absolute right-2 top-2 size-1.5 rounded-full bg-accent" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-10 gap-2 px-2" data-testid="user-menu-button">
                    <Avatar className="size-8"><AvatarFallback>{displayInitials}</AvatarFallback></Avatar>
                    <span className="hidden text-left md:block"><span className="block text-xs font-semibold capitalize" data-testid="user-display-name">{displayName}</span><span className="block text-[10px] capitalize text-muted-foreground" data-testid="user-role-display">{roleLabel}</span></span>
                    <ChevronDown className="hidden size-3 md:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-dropdown">
                  <DropdownMenuLabel data-testid="user-menu-identity">Signed in as {displayName}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="user-menu-profile"><UserRoundPlus className="mr-2 size-4" />Profile</DropdownMenuItem>
                  <DropdownMenuItem data-testid="user-menu-data"><PackageOpen className="mr-2 size-4" />Data overview</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} data-testid="user-menu-logout"><LogOut className="mr-2 size-4" />Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="mx-auto w-full max-w-[1600px] px-4 py-6 md:px-6 md:py-8 xl:px-8" data-testid="app-main-content">
            <Outlet />
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
};
